import React from 'react'

import { ICONS } from '../../constants'

import TextInput from '../../common/components/TextInput'
import TextArea from '../../common/components/TextArea'
import Select from '../../common/components/Select'
import Checkbox from '../../common/components/Checkbox'
import Button from '../../common/components/Button'

import IconSelect, { ICON_TYPES } from './IconSelect'
import PermissionsCheckList from '../permissions/components/PermissionsCheckList'
import RolesCheckList from '../roles/components/RolesCheckList'
import AILayerSelectList from '../aiLayers/components/AILayerSelectList'
import IncidentTypeSelectList from '../incidentTypes/components/IncidentTypeSelectList'
import IncidentCategorySelectList from '../incidentCategories/components/IncidentCategorySelectList'
import IncidentDivisionSelectList from '../incidentDivisions/components/IncidentDivisionSelectList'

export const FIELD_TYPES = {
  TEXT: 1,
  NUMBER: 2,
  SELECT: 3,
  TEXT_AREA: 4,
  CHECKBOX: 5,
  CHECKBOX_ARRAY: 6,
  CONSTANT: 7,
  AI_ICONS: 8,
  INCIDENT_ICONS: 9,
  INCIDENT_DIVISIONS: 10,
  INCIDENT_CATEGORIES: 11,
  PERMISSIONS_LIST: 12,
  AI_LAYERS_LIST: 13,
  INCIDENT_TYPE_LIST: 14,
  INCIDENT_CATEGORY_LIST: 15,
  INCIDENT_DIVISION_LIST: 16,
  ROLES_LIST: 17,
  CHECKBOX_EXPIRATION_ARRAY: 18,
}

const FormField = ({ item, field, onChange }) => {
  switch (field.type) {
    case FIELD_TYPES.CONSTANT:
      return (
        <div>
          <label>{field.label}</label>
          <span>{item[field.name]}</span>
        </div>
      )
    case FIELD_TYPES.TEXT:
      return (
        <TextInput
          label={field.label}
          value={item[field.name]}
          onChange={onChange(field.name)}
        />
      )
    case FIELD_TYPES.NUMBER:
      return (
        <TextInput
          label={field.label}
          type="number"
          min={field.min}
          max={field.max}
          value={item[field.name]}
          onChange={onChange(field.name)}
        />
      )
    case FIELD_TYPES.TEXT_AREA:
      return (
        <TextArea
          label={field.label}
          value={item[field.name]}
          cols={field.cols}
          rows={field.rows}
          onChange={onChange(field.name)}
        />
      )
    case FIELD_TYPES.SELECT:
      return (
        <Select
          label={field.label}
          value={item[field.name]}
          options={field.options}
          onChange={onChange(field.name)}
        />
      )
    case FIELD_TYPES.AI_ICONS:
      return (
        <IconSelect
          label={field.label}
          value={item[field.name]}
          onChange={onChange(field.name)}
          type={ICON_TYPES.AI}
          iconDir={ICONS.AI.BORDERLESS.DIR}
        />
      )
    case FIELD_TYPES.INCIDENT_ICONS:
      return (
        <IconSelect
          label={field.label}
          value={item[field.name]}
          onChange={onChange(field.name)}
          type={ICON_TYPES.INCIDENTS}
          iconDir={ICONS.BORDERLESS.DIR}
        />
      )
    case FIELD_TYPES.CHECKBOX_ARRAY:
      const onPermissionsChange = (id) => (checked) => {
        if (checked) {
          onChange(field.name)([...item[field.name], id])
        } else {
          onChange(field.name)(item[field.name].filter((i) => i !== id))
        }
      }

      return (
        <div>
          <label>{field.label}</label>
          <ul style={{ listStyle: 'none' }}>
            {field.options.map((option, i) => (
              <li key={i}>
                <Checkbox
                  label={option.label}
                  checked={item[field.name].includes(option.id)}
                  onChange={onPermissionsChange(option.id)}
                  hideContainer={true}
                />
              </li>
            ))}
          </ul>
        </div>
      )
    case FIELD_TYPES.CHECKBOX_EXPIRATION_ARRAY:
      const onRolesChange = (id) => (checked) => {
        if (checked) {
          onChange(field.name)([
            ...item[field.name],
            { id, expirationDate: null },
          ])
        } else {
          onChange(field.name)(item[field.name].filter((i) => i.id !== id))
        }
      }

      const onExpirationChange = (id) => (expirationDate) => {
        const index = item[field.name].findIndex((i) => i.id === id)
        if (index !== -1) {
          const updatedItem = { ...item[field.name][index], expirationDate }
          const updatedItems = [
            ...item[field.name].slice(0, index),
            updatedItem,
            ...item[field.name].slice(index + 1),
          ]
          onChange(field.name)(updatedItems)
        }
      }

      const onExpirationCopy = (expirationDate) => () => {
        const updatedItems = item[field.name].map((value) => ({
          ...value,
          expirationDate,
        }))
        onChange(field.name)(updatedItems)
      }

      return (
        <div>
          <label>{field.label}</label>
          <ul style={{ listStyle: 'none' }}>
            {field.options.map((option, i) => {
              const index = item[field.name].findIndex(
                (i) => i.id === option.id
              )
              if (index === -1) {
                return (
                  <li key={i}>
                    <Checkbox
                      label={option.label}
                      checked={false}
                      onChange={onRolesChange(option.id)}
                      hideContainer={true}
                    />
                  </li>
                )
              }
              const value = item[field.name][index]
              return (
                <li key={i} style={{ display: 'flex' }}>
                  <div style={{ flex: 1 }}>
                    <Checkbox
                      label={option.label}
                      checked={true}
                      onChange={onRolesChange(option.id)}
                      hideContainer={true}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      label={option.label2}
                      type="date"
                      value={value.expirationDate}
                      onChange={onExpirationChange(option.id)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Button
                      icon="fas fa-copy"
                      onClick={onExpirationCopy(value.expirationDate)}
                    />
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )
    case FIELD_TYPES.CHECKBOX:
      return (
        <Checkbox
          label={field.label}
          checked={item[field.name]}
          onChange={onChange(field.name)}
          hideContainer={true}
        />
      )
    case FIELD_TYPES.PERMISSIONS_LIST:
      return (
        <PermissionsCheckList field={field} item={item} onChange={onChange} />
      )
    case FIELD_TYPES.ROLES_LIST:
      return <RolesCheckList field={field} item={item} onChange={onChange} />
    case FIELD_TYPES.AI_LAYERS_LIST:
      return <AILayerSelectList field={field} item={item} onChange={onChange} />
    case FIELD_TYPES.INCIDENT_TYPE_LIST:
      return (
        <IncidentTypeSelectList field={field} item={item} onChange={onChange} />
      )
    case FIELD_TYPES.INCIDENT_CATEGORY_LIST:
      return (
        <IncidentCategorySelectList
          field={field}
          item={item}
          onChange={onChange}
        />
      )
    case FIELD_TYPES.INCIDENT_DIVISION_LIST:
      return (
        <IncidentDivisionSelectList
          field={field}
          item={item}
          onChange={onChange}
        />
      )
    default:
      return (
        <div>
          Unknown Field Type: {field.type} -- {field.name}
        </div>
      )
  }
}

export default FormField
